import { combineReducers } from 'redux';
import merge from 'lodash/merge';

import {
  fetchDevices,
  fetchDevicesStats,
  changeCurrentDeviceState,
  setAutorunEngineDelaySeconds,
  toggleWebasto,
  clearTooltip,
  lockDoors,
  setTooltip,
  toggleIgnition,
  resetDeviceLockDoors,
  resetDeviceToggleIgnition,
  resetDeviceUnlockTrunk,
  toggleTrunk,
  toggleServiceMode,
} from '../sagas/devices/actions';
import { changeCurrentDevice } from '../components/Header/actions';
import {
  ignitionClick,
  serviceModeClick,
  lockDoorsClick,
} from '../components/Sidebar/actions';
import { logoutButtonClick } from '../components/Auth/actions';
import { editDeviceNameSave } from '../components/Modal/SettingsModal/VehiclesSettings/actions';

const devices = (state = [], action) => {
  if (action.type === fetchDevices.done.type) {
    return action.payload.result.data;
  }

  if (action.type === editDeviceNameSave.type) {
    const { deviceId, name } = action.payload;
    return state.map(
      device => (device.id === deviceId ? { ...device, name } : device),
    );
  }

  if (action.type === logoutButtonClick.type) {
    return [];
  }

  return state;
};

const devicesStats = (state = [], action) => {
  if (action.type === fetchDevicesStats.done.type) {
    return action.payload.result.data;
  }

  if (action.type === logoutButtonClick.type) {
    return [];
  }

  return state;
};

const devicesConnectionLevels = (state = {}, action) => {
  if (action.type === fetchDevicesStats.done.type) {
    const devices = action.payload.result.data;
    return devices.reduce((prev, current) => {
      let quality = 1;
      if (current.gsmQualityRSSI >= 7 && current.gsmQualityRSSI <= 10) {
        quality = 2;
      }
      if (current.gsmQualityRSSI >= 11 && current.gsmQualityRSSI <= 13) {
        quality = 3;
      }
      if (current.gsmQualityRSSI >= 14 && current.gsmQualityRSSI <= 15) {
        quality = 4;
      }
      if (current.gsmQualityRSSI >= 16 && current.gsmQualityRSSI <= 31) {
        quality = 5;
      }
      prev[current.deviceId] = quality;
      return prev || { [current.deviceId]: quality };
    }, {});
  }

  if (action.type === logoutButtonClick.type) {
    return {};
  }

  return state;
};

const currentDevice = (state = {}, action) => {
  if (action.type === fetchDevices.done.type) {
    if (action.payload.result.data.length && !state.id)
      return action.payload.result.data[0];
    return state;
  }

  if (action.type === changeCurrentDevice.type) {
    return action.payload.device;
  }

  if (action.type === logoutButtonClick.type) {
    return {};
  }

  if (action.type === setAutorunEngineDelaySeconds.done.type) {
    const autorunEngineDelayInSeconds = JSON.parse(action.payload.params.body)
      .delay;

    return {
      ...state,
      autorunEngineDelayInSeconds,
    };
  }

  if (action.type === editDeviceNameSave.type) {
    const { deviceId, name } = action.payload;
    return state.id === deviceId
      ? {
          ...state,
          name,
        }
      : state;
  }

  return state;
};

const currentDeviceStats = (state = {}, action) => {
  if (action.type === fetchDevicesStats.done.type) {
    // if (state.deviceId || !action.payload.result.data.length) return state;
    return (
      action.payload.result.data
        .filter(item => item.deviceId === action.payload.params.currentDeviceId)
        .pop() || {}
    );
  }

  if (action.type === toggleServiceMode.done.type) {
    return merge(state, { serviceOn: !state.serviceOn });
  }

  if (action.type === toggleWebasto.done.type) {
    return merge(state, { webastoOn: !state.webastoOn });
  }

  if (action.type === changeCurrentDeviceState.type) {
    return action.payload;
  }

  if (action.type === logoutButtonClick.type) {
    return {};
  }

  if (action.type === toggleWebasto.done.type) {
    const webastoOn = JSON.parse(action.payload.params.body).on;

    return {
      ...state,
      webastoOn,
    };
  }

  return state;
};

const lockDisabled = (state = false, action) => {
  if (action.type === lockDoorsClick.type) {
    return true;
  }

  if (
    action.type === fetchDevicesStats.done.type ||
    action.type === fetchDevices.failed.type
  ) {
    return false;
  }

  return state;
};

const ignitionDisabled = (state = false, action) => {
  if (action.type === ignitionClick.type) {
    return true;
  }

  if (
    action.type === fetchDevicesStats.done.type ||
    action.type === fetchDevices.failed.type
  ) {
    return false;
  }

  return state;
};

const serviceModeDisabled = (state = false, action) => {
  if (action.type === serviceModeClick.type) {
    return true;
  }

  if (
    action.type === fetchDevicesStats.done.type ||
    action.type === fetchDevices.failed.type
  ) {
    return false;
  }

  return state;
};

const lockDoorsState = (state = {}, action) => {
  if (action.type === lockDoors.done.type) {
    return {
      ...state,
      [action.payload.params.deviceId]: Date.now(),
    };
  }
  if (action.type === resetDeviceLockDoors.type) {
    return {
      ...state,
      [action.payload.deviceId]: 0,
    };
  }
  return state;
};

const unlockTrunkState = (state = {}, action) => {
  if (action.type === toggleTrunk.done.type) {
    return {
      ...state,
      [action.payload.params.deviceId]: Date.now(),
    };
  }
  if (action.type === resetDeviceUnlockTrunk.type) {
    return {
      ...state,
      [action.payload.deviceId]: 0,
    };
  }
  return state;
};

const ignitionState = (state = {}, action) => {
  if (action.type === toggleIgnition.done.type) {
    return {
      ...state,
      [action.payload.params.deviceId]: Date.now(),
    };
  }
  if (action.type === resetDeviceToggleIgnition.type) {
    return {
      ...state,
      [action.payload.deviceId]: 0,
    };
  }
  return state;
};

const tooltip = (state = null, action) => {
  if (
    action.type === clearTooltip.type ||
    action.type === changeCurrentDevice.type
  ) {
    return null;
  }

  if (action.type === setTooltip.type) {
    return {
      type: action.payload.type,
      on: action.payload.on || null,
    };
  }

  return state;
};

export default combineReducers({
  devices,
  devicesStats,
  devicesConnectionLevels,
  currentDevice,
  currentDeviceStats,
  lockDisabled,
  ignitionDisabled,
  serviceModeDisabled,
  lockDoorsState,
  ignitionState,
  unlockTrunkState,
  tooltip,
});



// WEBPACK FOOTER //
// ./src/reducers/devices.js